import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardCheck,
  ClipboardX,
  FileText,
  Sparkles,
  Upload,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextEditor } from '@/components/rich-text';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { StepContainer } from '@/pages/courses/components/add-assignment/step-container';
import { StepHeader, type StepMetaItem } from '@/pages/courses/components/add-assignment/step-header';
import {
  useCreateCourseMutation,
  useCreateCourseSectionMutation,
  useGetBoardsQuery,
  useGetUsersQuery,
  useGenerateStudyMaterialsMutation,
  useProcessCourseDocumentMutation,
} from '@/state/course';
import type { GenerateStudyMaterialsResponse } from '@/state/course';
import {
  buildLexicalDocument,
  createHeadingNode,
  createListNode,
  createParagraphNode,
} from '@/lib/lexical-builders';

type StudyMaterialSection = GenerateStudyMaterialsResponse['data']['study_materials'][number];

const buildObjectivesDocument = (items: string[]) =>
  buildLexicalDocument(items.length ? [createListNode(items)] : [createParagraphNode('')]);

const buildOverviewDocument = (text: string) =>
  buildLexicalDocument(text ? [createParagraphNode(text)] : [createParagraphNode('')]);

const buildSectionDocument = (section: StudyMaterialSection) => {
  const nodes = [];
  nodes.push(createHeadingNode('Overview', 'h3'));
  nodes.push(createParagraphNode(section.section_overview || ''));
  nodes.push(createHeadingNode('Learning Objectives', 'h3'));
  nodes.push(createListNode(section.learning_objectives || []));
  if (section.prerequisites?.length) {
    nodes.push(createHeadingNode('Prerequisites', 'h3'));
    nodes.push(createListNode(section.prerequisites));
  }
  if (section.detailed_explanations) {
    nodes.push(createHeadingNode('Detailed Explanations', 'h3'));
    section.detailed_explanations
      .split(/\n+/)
      .filter(Boolean)
      .forEach((chunk) => nodes.push(createParagraphNode(chunk)));
  }
  return buildLexicalDocument(nodes);
};

const GenerateCoursePage = () => {
  const navigate = useNavigate();
  const { tenant } = useParams();
  const { hasAIModule, isTeacher, isSchoolAdmin } = useClientPermissions();
  const canAccess = hasAIModule && (isTeacher || isSchoolAdmin);

  const [currentStep, setCurrentStep] = useState(0);

  const [formFile, setFormFile] = useState<File | null>(null);
  const [courseName, setCourseName] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [level, setLevel] = useState('');
  const [chapterCount, setChapterCount] = useState('');
  const [fullMarks, setFullMarks] = useState('');
  const [lecturer, setLecturer] = useState('');
  const [board, setBoard] = useState('');

  const [threadId, setThreadId] = useState<string | null>(null);
  const [overview, setOverview] = useState('');
  const [learningObjectives, setLearningObjectives] = useState('');
  const [chapters, setChapters] = useState<string[]>([]);
  const overviewRef = useRef<(() => string) | null>(null);
  const learningObjectivesRef = useRef<(() => string) | null>(null);

  const [courseId, setCourseId] = useState<number | null>(null);
  const [studyMaterials, setStudyMaterials] = useState<StudyMaterialSection[]>([]);
  const [sectionDrafts, setSectionDrafts] = useState<string[]>([]);
  const [sectionStatuses, setSectionStatuses] = useState<Record<number, 'pending' | 'accepted' | 'rejected'>>({});
  const sectionBriefRefs = useRef<Record<number, () => string>>({});

  const { data: boards = [] } = useGetBoardsQuery(tenant ? { tenant } : skipToken);
  const { data: users = [] } = useGetUsersQuery(tenant ? { tenant } : skipToken);

  const [createCourse, { isLoading: isCreatingCourse }] = useCreateCourseMutation();
  const [createCourseSection, { isLoading: isCreatingSection }] = useCreateCourseSectionMutation();
  const [processCourseDocument, { isLoading: isProcessingDocument }] =
    useProcessCourseDocumentMutation();
  const [generateStudyMaterials, { isLoading: isGeneratingMaterials }] =
    useGenerateStudyMaterialsMutation();
  const isBusy =
    isProcessingDocument || isCreatingCourse || isGeneratingMaterials || isCreatingSection;

  const loadingMessage = useMemo(() => {
    if (isProcessingDocument) return 'Reading your curriculum and extracting the course structure...';
    if (isCreatingCourse) return 'Setting up your course shell and preparing the learning path...';
    if (isGeneratingMaterials) return 'Generating study materials, lesson by lesson...';
    if (isCreatingSection) return 'Saving selected sections into the course...';
    return '';
  }, [isProcessingDocument, isCreatingCourse, isGeneratingMaterials, isCreatingSection]);

  const steps: StepMetaItem[] = [
    { title: 'Upload & Details', icon: Upload },
    { title: 'Review Course', icon: FileText },
    { title: 'Study Materials', icon: ClipboardCheck },
  ];

  const instructors = useMemo(
    () =>
      users.filter(
        (user) =>
          user.roles?.includes('TEACHER') ||
          user.roles?.includes('SCHOOL ADMIN') ||
          user.user_in_client_roles?.includes('TEACHER') ||
          user.user_in_client_roles?.includes('SCHOOL ADMIN')
      ),
    [users]
  );

  const getInstructorLabel = (teacher: (typeof instructors)[number]) => {
    const firstName = teacher.profile?.first_name;
    const lastName = teacher.profile?.last_name;
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || teacher.email || 'Instructor';
  };

  const handleProcessDocument = async () => {
    if (!tenant) return;
    if (!formFile) {
      toast.error('Attach a course document.');
      return;
    }
    if (!courseName || !courseCode || !level || !chapterCount || !fullMarks || !lecturer || !board) {
      toast.error('Fill all required fields.');
      return;
    }

    const formData = new FormData();
    formData.append('file', formFile);
    formData.append('course_name', courseName);
    formData.append('course_code', courseCode);
    formData.append('level', level);
    formData.append('number_of_chapters', chapterCount);
    formData.append('full_marks', fullMarks);
    formData.append('lecturer', lecturer);
    formData.append('board', board);

    try {
      const payload = await processCourseDocument({ tenant, data: formData }).unwrap();
      const data = payload.data;
      setThreadId(data.thread_id);
      setCourseName(data.course_name);
      setCourseCode(data.course_code);
      setLevel(data.level);
      setChapterCount(String(data.number_of_chapters));
      setBoard(data.board);
      setLecturer(data.lecturer);
      setFullMarks(String(data.full_marks));
      setChapters(data.chapters.map((chapter) => chapter.title));
      setOverview(buildOverviewDocument(data.course_summary));
      setLearningObjectives(buildObjectivesDocument(data.learning_objectives));
      setCurrentStep(1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to process course document.');
    }
  };

  const handleGenerateMaterials = async () => {
    if (!tenant || !threadId) return;
    try {
      const courseOverviewValue = overviewRef.current?.() ?? overview;
      const learningObjectivesValue = learningObjectivesRef.current?.() ?? learningObjectives;
      const createdCourse = await createCourse({
        tenant,
        data: {
          course_name: courseName,
          course_code: courseCode,
          full_marks: fullMarks,
          lecturer,
          board: Number(board),
          course_overview: courseOverviewValue,
          learning_objectives: learningObjectivesValue,
        },
      }).unwrap();
      setCourseId(createdCourse.id);

      const payload = await generateStudyMaterials({
        tenant,
        data: {
          thread_id: threadId,
          chapters: chapters.map((title) => ({ title })),
        },
      }).unwrap();
      const materials = payload.data?.study_materials ?? [];
      setStudyMaterials(materials);
      setSectionDrafts(materials.map((section: StudyMaterialSection) => buildSectionDocument(section)));
      const statusMap: Record<number, 'pending' | 'accepted' | 'rejected'> = {};
      materials.forEach((_: StudyMaterialSection, index: number) => {
        statusMap[index] = 'pending';
      });
      setSectionStatuses(statusMap);
      setCurrentStep(2);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate study materials.');
    }
  };

  const handleAcceptSection = async (index: number) => {
    if (!tenant || !courseId) return;
    const section = studyMaterials[index];
    if (!section) return;

    const sectionBrief = sectionBriefRefs.current[index]?.();
    if (!sectionBrief) {
      toast.error('Section content is not ready yet.');
      return;
    }
    try {
      await createCourseSection({
        tenant,
        data: {
          course: String(courseId),
          section_name: section.section_name,
          section_order: index + 1,
          section_summary: null,
          section_brief: sectionBrief,
        },
      }).unwrap();
      setSectionStatuses((prev) => ({ ...prev, [index]: 'accepted' }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create course section.');
    }
  };

  const handleRejectSection = (index: number) => {
    setSectionStatuses((prev) => ({ ...prev, [index]: 'rejected' }));
  };

  const handleAcceptAll = async () => {
    for (let i = 0; i < studyMaterials.length; i += 1) {
      if (sectionStatuses[i] === 'rejected' || sectionStatuses[i] === 'accepted') continue;
      await handleAcceptSection(i);
    }
  };

  if (!canAccess) {
    return (
      <div className="space-y-4 px-2 py-6 sm:px-4">
        <div className="rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
          <p className="text-lg font-semibold">Access restricted</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Only teachers with AI module access can generate courses.
          </p>
          <Button type="button" variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl space-y-6 px-2 py-5 sm:px-4 lg:px-8">
      {isBusy && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/75 backdrop-blur-md px-4">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-border/60 bg-card/95 p-6 shadow-2xl shadow-black/20 dark:bg-slate-950/95">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-emerald-500/10" />
            <div className="relative flex flex-col items-center text-center">
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/15">
                <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary/20 border-t-primary" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                AI course wizard
              </p>
              <h3 className="mt-2 text-xl font-bold text-foreground">
                Working on your course
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {loadingMessage}
              </p>
              <div className="mt-5 flex w-full items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-muted">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-primary/70" />
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Please wait
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border bg-gradient-to-r from-slate-50 via-white to-slate-100/80 p-4 shadow-sm dark:from-slate-950 dark:via-slate-900/80 dark:to-slate-950/80 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI course generator</p>
            <p className="text-lg font-semibold sm:text-2xl">Generate course</p>
            <p className="text-sm text-muted-foreground">Create a full course from a curriculum document.</p>
          </div>
          <Button type="button" variant="secondary" className="inline-flex items-center gap-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <StepHeader currentStep={currentStep} steps={steps} label="Course wizard" />

      {currentStep === 0 ? (
        <StepContainer key="step-0" animationKey={currentStep}>
          <div className="flex min-h-[520px] flex-col gap-6">
            <div className="rounded-2xl bg-transparent p-0 shadow-none">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4 text-primary" />
                Course details
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-sm font-medium">Course Name *</label>
                  <Input value={courseName} onChange={(e) => setCourseName(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Course Code *</label>
                  <Input value={courseCode} onChange={(e) => setCourseCode(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Level *</label>
                  <Input value={level} onChange={(e) => setLevel(e.target.value)} placeholder="Beginner" />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Number of Chapters *</label>
                  <Input value={chapterCount} onChange={(e) => setChapterCount(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Full Marks *</label>
                  <Input value={fullMarks} onChange={(e) => setFullMarks(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Select Instructor *</label>
                  <Select value={lecturer} onValueChange={setLecturer}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Instructor" />
                    </SelectTrigger>
                    <SelectContent className="min-w-[var(--radix-select-trigger-width)]">
                      {instructors.map((teacher) => (
                        <SelectItem key={teacher.id} value={teacher.id}>
                          {getInstructorLabel(teacher)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Select Board *</label>
                  <Select value={board} onValueChange={setBoard}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Board" />
                    </SelectTrigger>
                    <SelectContent className="min-w-[var(--radix-select-trigger-width)]">
                      {boards.map((boardItem) => (
                        <SelectItem key={boardItem.id} value={String(boardItem.id)}>
                          {boardItem.board_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-transparent p-0 shadow-none">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Upload className="h-4 w-4 text-primary" />
                Upload Course Curriculum
              </div>
              <div className="mt-4 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/20 p-5 text-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">Drop your curriculum PDF here</p>
                    <p className="text-xs text-muted-foreground">or click to browse files</p>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <label className="cursor-pointer">
                      Browse file
                      <Input
                        type="file"
                        className="hidden"
                        onChange={(event) => setFormFile(event.target.files?.[0] ?? null)}
                      />
                    </label>
                  </Button>
                </div>
                {formFile ? (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-background px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{formFile.name}</span>
                    </div>
                    <button
                      type="button"
                      className="text-sm font-medium text-primary hover:underline"
                      onClick={() => {
                        const fileUrl = URL.createObjectURL(formFile);
                        window.open(fileUrl, '_blank', 'noopener,noreferrer');
                        window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
                      }}
                    >
                      View file
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-auto flex items-center justify-end gap-2 border-t pt-4">
              <Button type="button" onClick={handleProcessDocument} disabled={isBusy}>
                Continue
              </Button>
            </div>
          </div>
        </StepContainer>
      ) : null}

      {currentStep === 1 ? (
        <StepContainer key="step-1" animationKey={currentStep}>
          <div className="flex min-h-[520px] flex-col gap-6">
            <div className="rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
              <p className="text-lg font-semibold">Course Overview</p>
              <div className="mt-3">
                <RichTextEditor
                  value={overview}
                  onChange={setOverview}
                  onProvideGetSerializedState={(getState) => {
                    overviewRef.current = getState;
                  }}
                />
              </div>
            </div>
            <div className="rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
              <p className="text-lg font-semibold">Learning Objectives</p>
              <div className="mt-3">
                <RichTextEditor
                  value={learningObjectives}
                  onChange={setLearningObjectives}
                  onProvideGetSerializedState={(getState) => {
                    learningObjectivesRef.current = getState;
                  }}
                />
              </div>
            </div>
            <div className="rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold">Chapters ({chapters.length})</p>
              </div>
              <div className="mt-4 space-y-3">
                {chapters.map((chapter, index) => (
                  <div key={`${chapter}-${index}`} className="flex items-center gap-2 rounded-xl border bg-muted/30 px-3 py-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      {index + 1}
                    </span>
                    <Input
                      value={chapter}
                      onChange={(event) =>
                        setChapters((prev) =>
                          prev.map((value, idx) => (idx === index ? event.target.value : value))
                        )
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setChapters((prev) => prev.filter((_, idx) => idx !== index))}
                    >
                      <XCircle className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-auto flex items-center justify-between border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setCurrentStep(0)}>
                Back
              </Button>
              <Button
                type="button"
                onClick={handleGenerateMaterials}
                disabled={isBusy}
              >
                Generate materials
              </Button>
            </div>
          </div>
        </StepContainer>
      ) : null}

      {currentStep === 2 ? (
        <StepContainer key="step-2" animationKey={currentStep}>
          <div className="flex min-h-[520px] flex-col gap-6">
            <div className="rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-lg font-semibold">Study Materials Generated</p>
                  <p className="text-sm text-muted-foreground">Review, accept, or reject each section below.</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleAcceptAll}
                  disabled={isCreatingSection}
                  className="border border-emerald-200 text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  Accept All Pending
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Accepted: {Object.values(sectionStatuses).filter((s) => s === 'accepted').length}
                </span>
                <span className="inline-flex items-center gap-2">
                  <ClipboardX className="h-4 w-4 text-rose-500" /> Rejected: {Object.values(sectionStatuses).filter((s) => s === 'rejected').length}
                </span>
                <span className="inline-flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4 text-muted-foreground" /> Pending: {Object.values(sectionStatuses).filter((s) => s === 'pending').length}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {studyMaterials.map((section, index) => {
                const status = sectionStatuses[index] ?? 'pending';
                return (
                  <div key={`${section.section_name}-${index}`} className="rounded-2xl border bg-card p-4 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-base font-semibold">{section.section_name}</p>
                        </div>
                      </div>
                      {status === 'pending' ? (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleAcceptSection(index)}
                            className="border border-emerald-200 text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                          >
                            Accept
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRejectSection(index)}
                            className="border border-rose-200 text-rose-600 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                          >
                            Reject
                          </Button>
                        </div>
                      ) : status === 'accepted' ? (
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
                          Accepted
                        </span>
                      ) : (
                        <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
                          Rejected
                        </span>
                      )}
                    </div>

                    <div className="mt-4">
                      <RichTextEditor
                        value={sectionDrafts[index] ?? buildSectionDocument(section)}
                        onChange={(value) => {
                          setSectionDrafts((prev) => {
                            const clone = [...prev];
                            clone[index] = value;
                            return clone;
                          });
                        }}
                        onProvideGetSerializedState={(getState) => {
                          sectionBriefRefs.current[index] = getState;
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-auto flex items-center justify-end border-t pt-4">
              <Button
                type="button"
                onClick={() => {
                  if (!tenant || !courseId) return;
                  navigate(`/${tenant}/courses/${courseId}`);
                }}
                disabled={!tenant || !courseId || isBusy}
              >
                Browse to course
              </Button>
            </div>
          </div>
        </StepContainer>
      ) : null}
    </div>
  );
};

export default GenerateCoursePage;
